import { type Dispatch, type SetStateAction } from "react";
import { NarAgentGridTableColumnKeys } from "../../../../../enums/tableColumnKeys";

/**
 * Manages header select-all and per-row checkbox state for the NAR Agent Grid table.
 *
 * @param tableData - Current table row data
 * @param setTableData - State setter for table row data
 * @returns Select-all handler, row toggle handler, whether every row is selected, and whether any row is selected
 */
export const useNarAgentGridSelectAll = (
  tableData: any[],
  setTableData: Dispatch<SetStateAction<any[]>>,
) => {
  const selectKey = NarAgentGridTableColumnKeys.Select;

  const isCheckboxSelected =
    tableData.length > 0 &&
    tableData.every((row) => row[selectKey]?.isChecked === true);

  const hasSelectedRows = tableData.some(
    (row) => row[selectKey]?.isChecked === true,
  );

  const selectedRowCount = tableData.filter(
    (row) => row[selectKey]?.isChecked === true,
  ).length;

  /**
   * Toggles every row checkbox to the inverse of the current all-selected state.
   */
  const handleSelectAll = () => {
    const nextChecked = !isCheckboxSelected;
    setTableData((prevData) =>
      prevData.map((row) => ({
        ...row,
        [selectKey]: {
          ...row[selectKey],
          isChecked: nextChecked,
        },
      })),
    );
  };

  /**
   * Toggles a single row checkbox identified by its auth-grid identifier.
   *
   * @param id - Row identifier from the checkbox cell payload
   */
  const toggleCheckboxSelection = (id: string) => {
    setTableData((prevData) =>
      prevData.map((row) =>
        row[selectKey]?.id === id
          ? {
              ...row,
              [selectKey]: {
                ...row[selectKey],
                isChecked: !row[selectKey]?.isChecked,
              },
            }
          : row,
      ),
    );
  };

  return {
    handleSelectAll,
    toggleCheckboxSelection,
    isCheckboxSelected,
    hasSelectedRows,
    selectedRowCount,
  };
};
